import React from "react";
import * as Icons from "lucide-react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight } from "lucide-react";
import PageHero from "../components/PageHero";
import ContactForm from "../components/ContactForm";
import { Seo } from "../components/Seo";
import { RelatedServices } from "../components/RelatedServices";
import { Button } from "../components/ui/button";
import { seoComponents, seoHighlights, images } from "../mock";

const AutoEngineCta = () => (
  <section className="py-8">
    <div className="container-wide">
      <div className="relative overflow-hidden rounded-3xl bg-slate-900 text-white p-8 sm:p-10">
        <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-primary/20 blur-3xl" />
        <div className="relative flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary/20 px-4 py-1.5 text-xs font-semibold text-primary">
              <Sparkles className="h-3.5 w-3.5" /> New — AI-Powered Tool
            </span>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl font-extrabold tracking-tight">
              Try our SEO Auto Engine
            </h2>
            <p className="mt-3 text-slate-300 leading-relaxed">
              Enter any website URL and get an instant AI audit — on-page score, keyword
              opportunities, optimised meta tags and a prioritised fix list. Automated, in seconds.
            </p>
          </div>
          <Button asChild size="lg" className="rounded-xl bg-primary hover:bg-primary/90 shadow-lg shadow-primary/25 shrink-0">
            <Link to="/seo/auto-engine">
              Launch SEO Auto Engine <ArrowRight className="h-4 w-4 ml-2" />
            </Link>
          </Button>
        </div>
      </div>
    </div>
  </section>
);

const WhatIsSeoSection = () => (
  <section className="py-20">
    <div className="container-wide grid lg:grid-cols-2 gap-12 items-center">
      <div className="rounded-3xl overflow-hidden shadow-xl order-last lg:order-first">
        <img
          src={images.seo2}
          alt="Marketing team reviewing an organic search traffic growth chart during an SEO strategy meeting"
          loading="lazy"
          decoding="async"
          className="w-full h-[360px] object-cover"
        />
      </div>
      <div>
        <span className="inline-block text-xs font-semibold uppercase tracking-[0.18em] text-primary mb-3">What is SEO?</span>
        <h2 className="font-display text-3xl font-extrabold text-slate-900 text-balance">Search engine optimisation: be found by the right customers</h2>
        <p className="mt-5 text-slate-500 leading-relaxed">
          Search engine optimisation (SEO) is the practice of improving your website so it ranks higher on Google, Bing,
          Yahoo and every other search engine your customers use. Higher rankings mean more organic (non-paid) traffic —
          visitors who are actively searching for the products and services you offer.
        </p>
        <p className="mt-4 text-slate-500 leading-relaxed">
          Effective SEO combines technical health, keyword research, high-quality content, on-page optimisation and
          authoritative backlinks. Done right, it compounds over time: unlike ads, the traffic you earn keeps flowing
          without paying for every click.
        </p>
      </div>
    </div>
  </section>
);

const WhySeoMattersSection = () => (
  <section className="py-20 bg-white border-t border-slate-100" data-testid="seo-why-section">
    <div className="container-wide grid gap-12 lg:grid-cols-2">
      <div>
        <span className="inline-block text-xs font-semibold uppercase tracking-[0.18em] text-primary mb-3">Why It Matters</span>
        <h2 className="font-display text-3xl font-extrabold text-slate-900 text-balance">Why your business needs professional SEO services</h2>
        <div className="mt-6 space-y-4 text-slate-600 leading-relaxed">
          <p>
            More than 90% of online journeys start with a search engine, and the majority of clicks go to the first page
            of results. If your website isn't ranking for the keywords your customers type, that traffic — and revenue —
            goes to your competitors. Professional SEO services close that gap systematically: fixing technical issues
            that block crawlers, targeting keywords with real commercial intent, and structuring your content so search
            engines clearly understand what you offer.
          </p>
          <p>
            Our approach starts with data, not guesswork. Every engagement begins with a comprehensive audit of your
            titles, meta descriptions, heading hierarchy, page speed signals, internal linking and content quality. Want
            a taste right now? Run your website through our free{" "}
            <Link to="/seo/auto-engine" className="font-semibold text-primary hover:underline">AI-powered SEO Auto Engine</Link>{" "}
            — it crawls your pages and delivers an instant scored audit with keyword opportunities and AI-optimised meta tags.
          </p>
        </div>
      </div>
      <div className="space-y-4 text-slate-600 leading-relaxed lg:pt-24">
        <p>
          From there we build a prioritised optimisation roadmap: on-page fixes ranked by impact, a keyword strategy
          mapped to every stage of the buying journey, content recommendations that answer what your customers are
          actually searching for, and ethical white-hat link building that strengthens your domain authority across
          Google, Bing and beyond.
        </p>
        <p>
          You'll always know what we're doing and why. Transparent monthly reporting tracks rankings, organic traffic
          and conversions — the metrics that matter to your bottom line, not vanity numbers.{" "}
          <a href="#contact" className="font-semibold text-primary hover:underline">Request a free SEO consultation</a>{" "}
          and find out how much organic growth your website is leaving on the table.
        </p>
      </div>
    </div>
  </section>
);

const KeyComponentsSection = () => (
  <section className="py-20 bg-slate-50">
    <div className="container-wide">
      <div className="max-w-2xl mx-auto text-center">
        <span className="inline-block text-xs font-semibold uppercase tracking-[0.18em] text-primary mb-3">Our Approach</span>
        <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 text-balance">Key components of SEO</h2>
      </div>
      <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {seoComponents.map((c) => (
          <div key={c.num} className="rounded-2xl bg-white p-7 border border-slate-100 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
            <span className="font-display text-3xl font-extrabold text-primary/30">{c.num}</span>
            <h3 className="mt-3 font-display text-lg font-bold text-slate-900">{c.title}</h3>
            <p className="mt-2 text-sm text-slate-500 leading-relaxed">{c.body}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const HighlightsSection = () => (
  <section className="py-20">
    <div className="container-wide grid gap-6 md:grid-cols-3">
      {seoHighlights.map((h) => {
        const Icon = Icons[h.icon] || Icons.Circle;
        return (
          <div key={h.title} className="rounded-2xl bg-slate-900 text-white p-8">
            <span className="flex h-13 w-13 items-center justify-center rounded-2xl bg-primary/20 text-primary p-3">
              <Icon className="h-7 w-7" />
            </span>
            <h3 className="mt-5 font-display text-xl font-bold">{h.title}</h3>
            <p className="mt-3 text-sm text-slate-300 leading-relaxed">{h.body}</p>
          </div>
        );
      })}
    </div>
    <div className="container-wide mt-12">
      <div className="rounded-2xl bg-primary/5 border border-primary/10 p-8 text-center">
        <p className="text-slate-600 leading-relaxed max-w-3xl mx-auto">
          OzzieeTech provides the best tailor-made SEO plan that suits and meets all your expectations. Looking for an SEO team that is honest, transparent and focused on delivering quality results? Fill out the form below.
        </p>
      </div>
    </div>
  </section>
);

const SeoContactSection = () => (
  <section id="contact" className="py-20 bg-slate-50">
    <div className="container-wide">
      <div className="max-w-2xl mx-auto text-center mb-12">
        <span className="inline-block text-xs font-semibold uppercase tracking-[0.18em] text-primary mb-3">Contact Us</span>
        <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900">Drop us a line!</h2>
      </div>
      <ContactForm compact source="SEO" />
    </div>
  </section>
);

const SeoPage = () => (
  <div>
    <Seo
      title="SEO Services That Grow Rankings, Traffic & Revenue | OzzieeTech"
      description="Data-driven SEO services: technical audits, keyword research, on-page optimisation and ethical link building. Try our free AI-powered SEO audit tool today."
      path="/seo"
    />
    <PageHero
      eyebrow="SEO Services"
      title="SEO Services That Grow Rankings, Traffic & Revenue"
      sub="Data-driven search engine optimisation for Google, Bing and beyond — technical audits, keyword strategy and content that turns searches into customers."
      image={images.seo1}
      imageAlt="SEO specialist analysing search rankings and organic traffic on an SEO analytics dashboard"
    />
    <AutoEngineCta />
    <WhatIsSeoSection />
    <KeyComponentsSection />
    <HighlightsSection />
    <WhySeoMattersSection />
    <RelatedServices current="/seo" />
    <SeoContactSection />
  </div>
);

export default SeoPage;
